import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Truck, Shield, RefreshCw, Star } from "lucide-react";
import { shopApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import RecentlyViewed from "@/components/RecentlyViewed";
import TrendingNow from "@/components/TrendingNow";
import { useSeo } from "@/lib/seo";
import WallOfLove from "@/components/WallOfLove";
import SocialStrip from "@/components/SocialStrip";

const CATEGORY_IMAGES = {
  "earbuds": "https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=600",
  "headphones": "https://images.unsplash.com/photo-1761005653657-0885604ddbcd?w=600",
  "speakers": "https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=600",
  "smartwatches": "https://images.unsplash.com/photo-1546868871-7041f2a55e12?w=600",
  "chargers": "https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=600",
  "power-banks": "https://images.unsplash.com/photo-1566554738544-d962991c3fee?w=600",
  "phone-accessories": "https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?w=600",
  "gaming-gear": "https://images.unsplash.com/photo-1600294037681-c80b4cb5b434?w=600",
  "keyboards": "https://images.unsplash.com/photo-1626958390943-a70309376444?w=600",
  "mice": "https://images.unsplash.com/photo-1527814050087-3793815479db?w=600",
  "cameras": "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=600",
  "smart-home": "https://images.unsplash.com/photo-1519558260268-cde7e03a0152?w=600",
};

export default function Home() {
  const { data: featured = [] } = useQuery({ queryKey: ["featured"], queryFn: () => shopApi.products({ featured: true, limit: 8 }) });
  const { data: newArrivals = [] } = useQuery({ queryKey: ["new"], queryFn: () => shopApi.products({ sort: "newest", limit: 4 }) });
  const { data: categories = [] } = useQuery({ queryKey: ["cats"], queryFn: shopApi.categories });
  const { data: allProducts = [] } = useQuery({ queryKey: ["all-count"], queryFn: () => shopApi.products({ limit: 20 }) });
  // Hide the "Shop by category" section until the store has at least 8 products —
  // clicking a category with 0-1 items looks broken for cold ad traffic.
  const showCategories = allProducts.length >= 8;
  useSeo({
    title: "NovalynxTech — Premium electronics, curated & delivered fast",
    description: "Shop premium earbuds, headphones, smartwatches, smart-home gear and gaming accessories. Free shipping over $50, 12-month warranty, 30-day easy returns.",
    canonical: typeof window !== "undefined" ? window.location.origin + "/" : "",
  });

  return (
    <div>
      {/* HERO — universal tech marketplace look, gadget collage, clear conversion-focused headline */}
      <section className="relative overflow-hidden border-b border-white/5">
        {/* Background: 6-tile gadget collage — earbuds / smartwatch / speaker / charger / keyboard / camera */}
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 opacity-25 gap-0.5">
          {[
            "https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=800&q=70",   // earbuds
            "https://images.unsplash.com/photo-1546868871-7041f2a55e12?w=800&q=70",     // smartwatch
            "https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=800&q=70",  // speaker
            "https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=800&q=70",  // charger
            "https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=800&q=70",  // keyboard
            "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=800&q=70",  // camera
          ].map((src, i) => (
            <img key={i} src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
          ))}
        </div>
        {/* Overlays for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent" />
        {/* Amber accent glow */}
        <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-primary/20 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-24 md:py-36 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="chip mb-6 border-primary/40 text-primary">Premium electronics · shipped worldwide</span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tighter mb-6">
              Everything Tech.<br /><span className="text-primary">One store.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mb-8">
              Earbuds, smartwatches, chargers, gaming gear, smart home — hand-picked, quality-tested, and delivered fast.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/shop"><Button className="btn-primary h-12 px-8 text-base" data-testid="hero-shop-btn">Shop all <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
              <Link to="/shop?featured=true"><Button variant="outline" className="h-12 px-8 border-white/15 bg-transparent text-base hover:bg-white/5" data-testid="hero-featured-btn">Explore collections</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar — expanded 4 signals for a universal tech marketplace */}
      <section className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs sm:text-sm">
          <div className="flex items-center gap-2 justify-center text-muted-foreground"><Truck className="h-4 w-4 text-primary shrink-0" /> <span>Fast shipping</span></div>
          <div className="flex items-center gap-2 justify-center text-muted-foreground"><Shield className="h-4 w-4 text-primary shrink-0" /> <span>Secure checkout</span></div>
          <div className="flex items-center gap-2 justify-center text-muted-foreground"><Star className="h-4 w-4 text-primary shrink-0" fill="currentColor" /> <span>Quality tested</span></div>
          <div className="flex items-center gap-2 justify-center text-muted-foreground"><RefreshCw className="h-4 w-4 text-primary shrink-0" /> <span>30-day returns</span></div>
        </div>
      </section>

      {/* TRENDING NOW — social proof strip, sits high on the page */}
      <TrendingNow limit={3} days={30} />

      {/* CATEGORIES — hidden until store has 8+ products (empty categories look broken to cold ad traffic) */}
      {showCategories && (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-10">
          <p className="chip mb-3">Shop by category</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Find your next favorite</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {categories.slice(0, 12).map((c) => (
            <Link key={c.slug} to={`/shop?category=${c.slug}`} className="card-hover rounded-xl bg-card border border-white/5 overflow-hidden group" data-testid={`category-${c.slug}`}>
              <div className="aspect-square overflow-hidden bg-secondary/40">
                <img src={CATEGORY_IMAGES[c.slug]} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="px-3 py-2.5 text-center">
                <span className="text-xs sm:text-sm font-medium">{c.name}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* FEATURED — cleaner spacing */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 border-t border-white/5">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <p className="chip mb-3"><Star className="h-3 w-3 text-primary" fill="currentColor" /> Featured</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Editor's selection</h2>
          </div>
          <Link to="/shop?featured=true" className="text-sm text-primary hover:underline flex items-center gap-1">See all <ArrowRight className="h-3 w-3" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="featured-grid">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* Bundle CTA — cleaner, less busy */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl bg-card border border-white/10 p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{background: "radial-gradient(circle at 50% 0%, hsl(200 100% 50% / 0.3), transparent 60%)"}} />
          <div className="relative">
            <p className="chip mb-4">Bundle & save</p>
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-3">Buy 2, save 15% automatically.</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">Stack with code <code className="px-2 py-0.5 rounded bg-black text-primary text-sm">VOLT10</code> for another 10% off.</p>
            <Link to="/shop"><Button className="btn-primary h-11 px-6">Start bundling</Button></Link>
          </div>
        </div>
      </section>

      {/* WALL OF LOVE — homepage social proof */}
      <WallOfLove limit={6} />

      {/* SOCIAL STRIP — brand-presence beyond the ad */}
      <SocialStrip />

      {/* NEW ARRIVALS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-10">
          <p className="chip mb-3">Just landed</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">New arrivals</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {newArrivals.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
        <RecentlyViewed title="Pick up where you left off" />
      </section>
    </div>
  );
}
